import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { StatusBadge } from '@/components/StatusBadge';
import { ShipmentTimeline } from '@/components/ShipmentTimeline';
import { DocumentUpload } from '@/components/DocumentUpload';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Search, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyShipments({ user }) {
  const [loads, setLoads] = useState([]);
  const [filteredLoads, setFilteredLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLoads();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = loads.filter(load => 
        load.load_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        load.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        load.load_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredLoads(filtered);
    } else {
      setFilteredLoads(loads);
    }
  }, [searchTerm, loads]);

  const fetchLoads = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/loads/my-loads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoads(response.data);
      setFilteredLoads(response.data);
    } catch (error) {
      toast.error('Failed to fetch loads');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar user={user} />
      
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="my-shipments-page">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tighter uppercase mb-8">
          <span className="text-white">MY </span>
          <span className="text-[#00A6E8]">SHIPMENTS</span>
        </h1>
        
        {/* Search */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              type="text"
              placeholder="Search by load number, status, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 bg-[#0F172A] border-white/10 text-white"
              data-testid="search-input"
            />
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-20">
            <p className="text-slate-400">Loading shipments...</p>
          </div>
        ) : filteredLoads.length === 0 ? (
          <div className="text-center py-20" data-testid="no-shipments">
            <Package className="mx-auto text-slate-600 mb-4" size={64} />
            <p className="text-slate-400 text-lg">No shipments found</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="shipments-list">
            {filteredLoads.map((load) => (
              <div key={load.id} className="bg-[#0F172A] border border-white/10 rounded-lg p-6 hover:border-white/20 transition-colors" data-testid={`shipment-card-${load.load_number}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">{load.load_number}</h3>
                    <p className="text-slate-400 text-sm">{load.load_type}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={load.status} />
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-[#00A6E8] text-[#00A6E8]" data-testid={`upload-doc-btn-${load.load_number}`}>
                          <FileText size={16} className="mr-2" />
                          Upload Doc
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0F172A] border-white/10 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-white">Upload Document - {load.load_number}</DialogTitle>
                        </DialogHeader>
                        <DocumentUpload loadId={load.id} onUploadSuccess={fetchLoads} />
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                
                {/* Timeline */}
                <div className="mb-6 p-4 bg-[#0A0E1A] rounded-lg">
                  <ShipmentTimeline currentStatus={load.status} />
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  {/* Container Numbers - Show for ALL load types */}
                  <div className="col-span-full mb-4">
                    <p className="text-slate-400 mb-2">Container Number(s)</p>
                    <div className="flex flex-wrap gap-2">
                      {load.containers && load.containers.length > 0 ? (
                        load.containers.map((c, idx) => (
                          <span key={idx} className="px-3 py-1 bg-[#00A6E8]/10 text-[#00A6E8] text-sm font-semibold rounded">
                            {c.container_number || `Container ${idx + 1}`}
                          </span>
                        ))
                      ) : load.container_number ? (
                        <span className="px-3 py-1 bg-[#00A6E8]/10 text-[#00A6E8] text-sm font-semibold rounded">
                          {load.container_number}
                        </span>
                      ) : (
                        <span className="text-slate-500">N/A</span>
                      )}
                    </div>
                  </div>
                  
                  {load.load_type === 'Import' && (
                    <>
                      <div>
                        <p className="text-slate-400">Steamship Line</p>
                        <p className="text-white font-semibold">{load.steamship_line || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Rail Ramp</p>
                        <p className="text-white font-semibold">{load.rail_ramp || load.terminal || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Delivery Address</p>
                        <p className="text-white font-semibold">
                          {typeof load.delivery_address === 'object' && load.delivery_address
                            ? `${load.delivery_address.street || ''}, ${load.delivery_address.city || ''}, ${load.delivery_address.state || ''} ${load.delivery_address.zip || ''}`
                            : load.delivery_address || 'N/A'
                          }
                        </p>
                      </div>
                    </>
                  )}
                  
                  {load.load_type === 'Export' && (
                    <>
                      <div>
                        <p className="text-slate-400">SSL (Steamship Line)</p>
                        <p className="text-white font-semibold">{load.steamship_line || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Booking Number</p>
                        <p className="text-white font-semibold">{load.booking_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Loading Address</p>
                        <p className="text-white font-semibold">
                          {typeof load.loading_address === 'object' && load.loading_address
                            ? `${load.loading_address.street || ''}, ${load.loading_address.city || ''}, ${load.loading_address.state || ''} ${load.loading_address.zip || ''}`
                            : load.loading_address || 'N/A'
                          }
                        </p>
                      </div>
                    </>
                  )}
                  
                  {load.load_type === 'Rate Request' && (
                    <>
                      <div>
                        <p className="text-slate-400">Origin Terminal</p>
                        <p className="text-white font-semibold">{load.origin_terminal || load.rail_ramp || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Delivery City/State</p>
                        <p className="text-white font-semibold">{load.delivery_city_state || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Number of Containers</p>
                        <p className="text-white font-semibold">{load.number_of_containers || load.containers?.length || 1}</p>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <p className="text-slate-400">Container Size</p>
                    <p className="text-white font-semibold">{load.container_size || load.containers?.[0]?.container_size || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-400">Commodity</p>
                    <p className="text-white font-semibold">{load.commodity || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-400">Created</p>
                    <p className="text-white font-semibold">{new Date(load.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                
                {/* Accessorial Charges - Show for customers (NO Line Haul) */}
                <div className="mt-4 p-4 bg-[#1E293B]/50 rounded-lg">
                  <p className="text-[#00A6E8] text-xs uppercase tracking-wider font-semibold mb-3">Accessorial Charges</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Chassis Min:</span>
                      <span className="text-white ml-2">${load.chassis_charge || 90}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Chassis Split:</span>
                      <span className="text-white ml-2">${load.chassis_split_charge || 85}</span>
                    </div>
                    {load.overweight_charge > 0 && (
                      <div>
                        <span className="text-yellow-400">Overweight:</span>
                        <span className="text-yellow-400 ml-2">${load.overweight_charge}</span>
                      </div>
                    )}
                    {load.hazmat_charge > 0 && (
                      <div>
                        <span className="text-yellow-400">Hazmat:</span>
                        <span className="text-yellow-400 ml-2">${load.hazmat_charge}</span>
                      </div>
                    )}
                  </div>
                  {/* Status indicator for quotation - Show LH and Total AFTER quotation */}
                  {['Quoted', 'Approved', 'Dispatched', 'Picked Up', 'Delivered', 'Returned', 'Completed'].includes(load.status) && load.linehaul_override ? (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400">Line Haul:</span>
                        <span className="text-[#FFA726] font-bold">${load.linehaul_override}</span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-white font-semibold">TOTAL CHARGES:</span>
                        <span className="text-[#00A6E8] font-bold text-lg">${load.total_override || load.calculated_total}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-xs mt-3 italic">
                      Line Haul will be quoted by DLT operations team
                    </p>
                  )}
                </div>
                
                {/* Customer-Friendly Status Timeline */}
                {load.change_log && load.change_log.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">Shipment Progress</p>
                    <div className="flex flex-wrap gap-2">
                      {load.change_log
                        .filter(entry => ['Load Submitted', 'Status Updated', 'Line Haul Quoted'].includes(entry.action))
                        .slice(-5)
                        .map((entry, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs bg-[#1E293B]/30 px-3 py-1 rounded-full">
                            <div className="w-2 h-2 rounded-full bg-[#00A6E8]"></div>
                            <span className="text-slate-400">{new Date(entry.timestamp).toLocaleDateString()}</span>
                            <span className="text-white">{entry.action === 'Status Updated' ? entry.details : entry.action}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {load.special_instructions && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-slate-400 text-sm mb-1">Special Instructions</p>
                    <p className="text-white text-sm">{load.special_instructions}</p>
                  </div>
                )}
                
                {/* Document Upload/View Section */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-[#00A6E8]/50 text-[#00A6E8] hover:bg-[#00A6E8]/10"
                      >
                        <FileText size={16} className="mr-2" />
                        Documents ({load.documents?.length || 0}/5)
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-white">Documents - {load.load_number}</DialogTitle>
                      </DialogHeader>
                      <div className="mt-4">
                        <DocumentUpload
                          loadId={load.id}
                          documents={load.documents || []}
                          onDocumentsChange={(newDocs) => {
                            // Update local state
                            const updatedLoads = loads.map(l => 
                              l.id === load.id ? {...l, documents: newDocs} : l
                            );
                            setLoads(updatedLoads);
                          }}
                          disabled={load.status === 'Completed' || load.status === 'Rejected'}
                        />
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
